import React, { useState, useEffect } from 'react';
import { getSongs, uploadSong, deleteSong } from '../services/songService';
import { getVideos, uploadVideo, deleteVideo } from '../services/videoService';
import { getPlaylists, createPlaylist, updatePlaylist, deletePlaylist } from '../services/playlistService';
import { getNewUsersByDay, UserStatsByDate } from '../services/userService';
import { Song, Video, Playlist } from '../types';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface AdminProps {
  onLogout: () => void;
}

const Admin: React.FC<AdminProps> = ({ onLogout }) => {
  const [songs, setSongs] = useState<Song[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userStats, setUserStats] = useState<UserStatsByDate[]>([]);
  const [dateRange, setDateRange] = useState<number>(14); // Default to 14 days
  
  // State for song form
  const [songFile, setSongFile] = useState<File | null>(null);
  const [songFiles, setSongFiles] = useState<FileList | null>(null); // New state for multiple files
  const [songTitle, setSongTitle] = useState('');
  const [songArtist, setSongArtist] = useState('');
  const [songCategory, setSongCategory] = useState('');
  const [songUploading, setSongUploading] = useState(false);
  const [folderPlaylistName, setFolderPlaylistName] = useState(''); // New state for playlist name
  
  // State for video form
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoCategory, setVideoCategory] = useState('');
  const [videoUploading, setVideoUploading] = useState(false);
  
  // State for playlist form
  const [playlistName, setPlaylistName] = useState('');
  const [selectedSongIds, setSelectedSongIds] = useState<string[]>([]);
  const [editPlaylistId, setEditPlaylistId] = useState<string | null>(null);
  
  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [fetchedSongs, fetchedVideos, fetchedPlaylists, fetchedUserStats] = await Promise.all([
          getSongs(),
          getVideos(),
          getPlaylists(),
          getNewUsersByDay()
        ]);
        
        setSongs(fetchedSongs);
        setVideos(fetchedVideos);
        setPlaylists(fetchedPlaylists);
        setUserStats(fetchedUserStats);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);
  
  // Handle uploading a song
  const handleSongUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Song upload initiated', { songFile, songTitle, songArtist, songCategory });
    
    if (!songFile) {
      console.error('No song file selected');
      alert('No song file selected. Please choose a file.'); // User-friendly alert
      return;
    }
    
    try {
      // Show loading state
      setSongUploading(true);
      
      console.log('Calling uploadSong service...');
      const newSong = await uploadSong(songFile, {
        title: songTitle,
        artist: songArtist,
        category: songCategory ? songCategory.split(',').map(c => c.trim()) : []
      });
      
      console.log('Song uploaded successfully:', newSong);
      setSongs([...songs, newSong]);
      
      // Reset form
      setSongFile(null);
      setSongTitle('');
      setSongArtist('');
      setSongCategory('');
      
      // Reset loading state
      setSongUploading(false);
      
      // Show success message
      alert('Song uploaded successfully!');
    } catch (error) {
      console.error('Error uploading song:', error);
      
      // Create a detailed error message
      let errorMessage = 'Error uploading song: ';
      
      if (error instanceof Error) {
        errorMessage += error.message;
        console.error('Error stack:', error.stack);
      } else {
        errorMessage += String(error);
      }
      
      // Show detailed error in alert for debugging
      alert(`${errorMessage}\n\nPlease check the console for more details.`);
      
      // Reset loading state
      setSongUploading(false);
    }
  };
  
  // Handle uploading a song folder and creating a playlist
  const handleSongFolderUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Song folder upload initiated', { songFiles, folderPlaylistName });

    if (!songFiles || songFiles.length === 0) {
      console.error('No folder or files selected');
      alert('No folder selected or folder is empty. Please choose a folder with songs.');
      return;
    }

    setSongUploading(true);
    const uploadedSongIds: string[] = [];
    let newPlaylistName = folderPlaylistName.trim();

    // Attempt to get folder name from the relative path of the first file if playlist name is not provided
    if (!newPlaylistName && songFiles.length > 0) {
        const firstFile = songFiles[0];
        // @ts-ignore webkitRelativePath is non-standard but widely supported for directory uploads
        const relativePath = firstFile.webkitRelativePath;
        if (relativePath) {
            const pathParts = relativePath.split('/');
            if (pathParts.length > 1) {
                newPlaylistName = pathParts[0]; // Use the top-level folder name
            }
        }
    }
    
    // If still no playlist name, default to 'New Playlist from Folder'
    if (!newPlaylistName) {
        newPlaylistName = 'New Playlist from Folder';
    }

    try {
      for (let i = 0; i < songFiles.length; i++) {
        const file = songFiles[i];
        // Skip non-audio files if any (though accept attribute should filter most)
        if (!file.type.startsWith('audio/')) {
            console.log(`Skipping non-audio file: ${file.name}`);
            continue;
        }

        // Extract title from filename (without extension)
        const fileNameParts = file.name.split('.');
        fileNameParts.pop(); // Remove extension
        const titleFromFile = fileNameParts.join('.');

        console.log(`Uploading song: ${file.name}`);
        // We'll use a simplified version of single song upload logic here
        // Assuming no specific artist/category for folder upload for now, or it could be added as a form field
        const newSong = await uploadSong(file, {
          title: titleFromFile, // Use filename as title
          artist: '', // Or use a common artist for the folder if provided
          category: [] // Or use common categories for the folder if provided
        });
        uploadedSongIds.push(newSong.id);
        console.log(`Song ${file.name} uploaded successfully: ${newSong.id}`);
      }

      if (uploadedSongIds.length === 0) {
        alert('No audio files were found in the selected folder or failed to upload.');
        setSongUploading(false);
        return;
      }

      console.log('Creating playlist with songs:', uploadedSongIds);
      const newPlaylist = await createPlaylist(newPlaylistName, uploadedSongIds);
      setPlaylists([...playlists, newPlaylist]);

      alert(`Successfully uploaded ${uploadedSongIds.length} songs and created playlist: "${newPlaylistName}"`);

    } catch (error) {
      console.error('Error during folder upload and playlist creation:', error);
      let errorMessage = 'Error uploading folder: ';
      if (error instanceof Error) {
        errorMessage += error.message;
      } else {
        errorMessage += String(error);
      }
      alert(`${errorMessage}\n\nPlease check the console for more details.`);
    } finally {
      setSongUploading(false);
      setSongFiles(null); // Reset file list
      setFolderPlaylistName(''); // Reset playlist name input
    }
  };
  
  // Handle uploading a video
  const handleVideoUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Video upload initiated', { videoFile, videoTitle, videoCategory });
    
    if (!videoFile) {
      console.error('No video file selected');
      return;
    }
    
    try {
      // Show loading state
      setVideoUploading(true);
      
      console.log('Calling uploadVideo service...');
      const newVideo = await uploadVideo(videoFile, {
        title: videoTitle,
        category: videoCategory ? videoCategory.split(',').map(c => c.trim()) : []
      });
      
      console.log('Video uploaded successfully:', newVideo);
      setVideos([...videos, newVideo]);
      
      // Reset form
      setVideoFile(null);
      setVideoTitle('');
      setVideoCategory('');
      
      // Reset loading state
      setVideoUploading(false);
      
      // Show success message
      alert('Video uploaded successfully!');
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Error uploading video. Please check console for details.');
      setVideoUploading(false);
    }
  };
  
  // Handle creating or updating a playlist
  const handlePlaylistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editPlaylistId) {
        // Update existing playlist
        await updatePlaylist(editPlaylistId, {
          name: playlistName,
          songIds: selectedSongIds
        } as Playlist);
        
        // Refresh playlists
        const updatedPlaylists = await getPlaylists();
        setPlaylists(updatedPlaylists);
      } else {
        // Create new playlist
        const newPlaylist = await createPlaylist(playlistName, selectedSongIds);
        setPlaylists([...playlists, newPlaylist]);
      }
      
      // Reset form
      setPlaylistName('');
      setSelectedSongIds([]);
      setEditPlaylistId(null);
    } catch (error) {
      console.error('Error saving playlist:', error);
    }
  };
  
  // Handle editing a playlist
  const handleEditPlaylist = (playlist: Playlist) => {
    setEditPlaylistId(playlist.id);
    setPlaylistName(playlist.name);
    setSelectedSongIds(playlist.songIds);
  };
  
  // Handle deleting a playlist
  const handleDeletePlaylist = async (playlistId: string) => {
    if (!window.confirm('Are you sure you want to delete this playlist?')) {
      return;
    }
    
    try {
      await deletePlaylist(playlistId);
      setPlaylists(playlists.filter(p => p.id !== playlistId));
    } catch (error) {
      console.error('Error deleting playlist:', error);
    }
  };
  
  // Handle deleting a song
  const handleDeleteSong = async (songId: string) => {
    if (!window.confirm('Are you sure you want to delete this song?')) {
      return;
    }
    
    try {
      await deleteSong(songId);
      setSongs(songs.filter(s => s.id !== songId));
    } catch (error) {
      console.error('Error deleting song:', error);
    }
  };
  
  // Handle deleting a video
  const handleDeleteVideo = async (videoId: string) => {
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }
    
    try {
      await deleteVideo(videoId);
      setVideos(videos.filter(v => v.id !== videoId));
    } catch (error) {
      console.error('Error deleting video:', error);
    }
  };
  
  // Function to filter userStats based on dateRange
  const getFilteredUserStats = () => {
    if (!userStats.length) return [];
    
    // Take only the most recent X days
    return userStats.slice(-dateRange);
  };
  
  if (isLoading) {
    return <div className="admin-loading">Loading...</div>;
  }
  
  return (
    <div className="admin-panel-wrapper">
      <div className="admin-panel">
        <div className="admin-header">
          <h1>Admin Panel</h1>
          <button onClick={onLogout} className="admin-button logout">Logout</button>
        </div>
        
        <section className="admin-section">
          <h2>User Growth</h2>
          <div className="chart-filters">
            <div className="date-range-selector">
              <label>Time Period: </label>
              <select 
                value={dateRange} 
                onChange={(e) => setDateRange(Number(e.target.value))}
                className="date-range-select"
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={getFilteredUserStats()}
                margin={{
                  top: 20,
                  right: 20,
                  left: 10,
                  bottom: 60,
                }}
                barGap={2}
                barSize={35}
              >
                <XAxis 
                  dataKey="date" 
                  angle={-45} 
                  textAnchor="end"
                  tick={{ fill: 'white', fontSize: 12 }}
                  height={80}
                />
                <YAxis tick={{ fill: 'white' }} />
                <Tooltip
                  contentStyle={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.8)', 
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: 'white'
                  }}
                  labelStyle={{ color: 'white' }}
                />
                <Legend wrapperStyle={{ color: 'white' }} />
                <Bar dataKey="free" name="Free Users" stackId="a" fill="#82ca9d" />
                <Bar dataKey="trial" name="Trial Users" stackId="a" fill="#8884d8" />
                <Bar dataKey="paid" name="Paid Users" stackId="a" fill="#ffc658" />
              </BarChart>
            </ResponsiveContainer>
            <div className="chart-info">
              <p>New user registrations over the last {dateRange} days, by subscription status.</p>
            </div>
          </div>
        </section>
        
        <section className="admin-section">
          <h2>Upload Song</h2>
          <form onSubmit={handleSongUpload} className="admin-form">
            <div className="form-group">
              <label>
                Song File:
                <div className="file-input-container">
                  <input 
                    type="file" 
                    id="song-file-input"
                    accept="audio/*" 
                    onChange={(e) => setSongFile(e.target.files?.[0] || null)}
                    className="file-input"
                    required 
                  />
                  <label htmlFor="song-file-input" className="file-input-label">
                    {songFile ? songFile.name : 'Choose a file'}
                  </label>
                </div>
              </label>
            </div>
            <div className="form-group">
              <label>
                Title:
                <input 
                  type="text" 
                  value={songTitle} 
                  onChange={(e) => setSongTitle(e.target.value)}
                  required 
                />
              </label>
            </div>
            <div className="form-group">
              <label>
                Artist:
                <input 
                  type="text" 
                  value={songArtist} 
                  onChange={(e) => setSongArtist(e.target.value)}
                />
              </label>
            </div>
            <div className="form-group">
              <label>
                Categories (comma separated):
                <input 
                  type="text" 
                  value={songCategory} 
                  onChange={(e) => setSongCategory(e.target.value)}
                />
              </label>
            </div>
            <button 
              type="submit" 
              disabled={!songFile || songUploading} 
              className={`admin-button ${songUploading ? 'loading' : ''}`}
            >
              {songUploading ? 'Uploading...' : 'Upload Song'}
            </button>
          </form>
        </section>
        
        <section className="admin-section">
          <h2>Upload Song Folder</h2>
          <form onSubmit={handleSongFolderUpload} className="admin-form">
            <div className="form-group">
              <label>
                Song Folder:
                <div className="file-input-container">
                  <input
                    type="file"
                    id="song-folder-input"
                    // @ts-ignore
                    webkitdirectory="true"
                    directory="true"
                    multiple
                    onChange={(e) => setSongFiles(e.target.files)}
                    className="file-input"
                    required
                  />
                  <label htmlFor="song-folder-input" className="file-input-label">
                    {songFiles && songFiles.length > 0 ? `${songFiles.length} files selected` : 'Choose a folder'}
                  </label>
                </div>
              </label>
            </div>
            <div className="form-group">
              <label>
                Playlist Name (Optional - uses folder name if empty):
                <input
                  type="text"
                  value={folderPlaylistName}
                  onChange={(e) => setFolderPlaylistName(e.target.value)}
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={!songFiles || songFiles.length === 0 || songUploading}
              className={`admin-button ${songUploading ? 'loading' : ''}`}
            >
              {songUploading ? 'Uploading...' : 'Upload Folder & Create Playlist'}
            </button>
          </form>
        </section>
        
        <section className="admin-section">
          <h2>Upload Video</h2>
          <form onSubmit={handleVideoUpload} className="admin-form">
            <div className="form-group">
              <label>
                Video File:
                <div className="file-input-container">
                  <input 
                    type="file" 
                    id="video-file-input"
                    accept="video/*" 
                    onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                    className="file-input"
                    required 
                  />
                  <label htmlFor="video-file-input" className="file-input-label">
                    {videoFile ? videoFile.name : 'Choose a file'}
                  </label>
                </div>
              </label>
            </div>
            <div className="form-group">
              <label>
                Title:
                <input 
                  type="text" 
                  value={videoTitle} 
                  onChange={(e) => setVideoTitle(e.target.value)}
                  required 
                />
              </label>
            </div>
            <div className="form-group">
              <label>
                Categories (comma separated):
                <input 
                  type="text" 
                  value={videoCategory} 
                  onChange={(e) => setVideoCategory(e.target.value)}
                />
              </label>
            </div>
            <button 
              type="submit" 
              disabled={!videoFile || videoUploading} 
              className={`admin-button ${videoUploading ? 'loading' : ''}`}
            >
              {videoUploading ? 'Uploading...' : 'Upload Video'}
            </button>
          </form>
        </section>
        
        <section className="admin-section">
          <h2>{editPlaylistId ? 'Edit' : 'Create'} Playlist</h2>
          <form onSubmit={handlePlaylistSubmit} className="admin-form">
            <div className="form-group">
              <label>
                Playlist Name:
                <input 
                  type="text" 
                  value={playlistName} 
                  onChange={(e) => setPlaylistName(e.target.value)}
                  required 
                />
              </label>
            </div>
            <div className="form-group song-selection">
              <label>Select Songs:</label>
              <div className="song-list">
                {songs.map(song => (
                  <div key={song.id} className="song-item">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={selectedSongIds.includes(song.id)} 
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSongIds([...selectedSongIds, song.id]);
                          } else {
                            setSelectedSongIds(selectedSongIds.filter(id => id !== song.id));
                          }
                        }}
                      />
                      {song.title} {song.artist ? `- ${song.artist}` : ''}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="admin-button">
                {editPlaylistId ? 'Update' : 'Create'} Playlist
              </button>
              {editPlaylistId && (
                <button 
                  type="button" 
                  onClick={() => {
                    setPlaylistName('');
                    setSelectedSongIds([]);
                    setEditPlaylistId(null);
                  }}
                  className="admin-button cancel"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>
        
        <section className="admin-section">
          <h2>Manage Songs</h2>
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Artist</th>
                  <th>Categories</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {songs.length === 0 ? (
                  <tr>
                    <td colSpan={4}>No songs available</td>
                  </tr>
                ) : (
                  songs.map(song => (
                    <tr key={song.id}>
                      <td>{song.title}</td>
                      <td>{song.artist || '-'}</td>
                      <td>{song.category?.join(', ') || '-'}</td>
                      <td>
                        <button 
                          onClick={() => handleDeleteSong(song.id)}
                          className="admin-button delete"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
        
        <section className="admin-section">
          <h2>Manage Videos</h2>
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Thumbnail</th>
                  <th>Title</th>
                  <th>Categories</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {videos.length === 0 ? (
                  <tr>
                    <td colSpan={4}>No videos available</td>
                  </tr>
                ) : (
                  videos.map(video => (
                    <tr key={video.id}>
                      <td className="thumbnail-cell">
                        {video.thumbnailUrl ? (
                          <img 
                            src={video.thumbnailUrl} 
                            alt={`Thumbnail for ${video.title}`}
                            className="video-thumbnail"
                          />
                        ) : (
                          <div className="no-thumbnail">No thumbnail</div>
                        )}
                      </td>
                      <td>{video.title}</td>
                      <td>{video.category?.join(', ') || '-'}</td>
                      <td>
                        <button 
                          onClick={() => handleDeleteVideo(video.id)}
                          className="admin-button delete"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
        
        <section className="admin-section">
          <h2>Manage Playlists</h2>
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Songs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {playlists.length === 0 ? (
                  <tr>
                    <td colSpan={3}>No playlists available</td>
                  </tr>
                ) : (
                  playlists.map(playlist => (
                    <tr key={playlist.id}>
                      <td>{playlist.name}</td>
                      <td>{playlist.songIds.length}</td>
                      <td>
                        <button 
                          onClick={() => handleEditPlaylist(playlist)}
                          className="admin-button edit"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeletePlaylist(playlist.id)}
                          className="admin-button delete"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <style>{`
        .admin-panel-wrapper {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow-y: auto;
          background-color: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(10px);
        }
        
        .admin-panel {
          max-width: 1000px;
          margin: 0 auto;
          padding: 20px 20px 40px 20px;
          font-family: 'Manrope', sans-serif;
          color: white;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.6);
          position: relative;
        }
        
        body, html, #root {
          height: 100%;
          width: 100%;
          overflow-y: auto !important;
          overflow-x: hidden;
        }
        
        .admin-section {
          margin-bottom: 30px;
          padding: 20px;
          background-color: rgba(0, 0, 0, 0.5);
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }
        
        .admin-form {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
        }
        
        .form-group label {
          margin-bottom: 5px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
        }
        
        .form-group input[type="text"],
        .form-group input[type="file"] {
          padding: 10px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          background-color: rgba(0, 0, 0, 0.3);
          color: white;
          transition: all 0.3s ease;
        }
        
        .form-group input[type="text"]:focus,
        .form-group input[type="file"]:focus {
          border-color: rgba(255, 255, 255, 0.5);
          outline: none;
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
        }
        
        .song-list {
          max-height: 200px;
          overflow-y: auto;
          border: 1px solid rgba(255, 255, 255, 0.2);
          padding: 10px;
          border-radius: 8px;
          background-color: rgba(0, 0, 0, 0.3);
        }
        
        .song-item {
          padding: 8px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .song-item:last-child {
          border-bottom: none;
        }
        
        .form-actions {
          display: flex;
          gap: 10px;
        }
        
        .admin-button {
          padding: 10px 18px;
          background-color: rgba(0, 0, 0, 0.5);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 25px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Manrope', sans-serif;
          font-weight: 500;
        }
        
        .admin-button:hover {
          background-color: rgba(255, 255, 255, 0.2);
          transform: scale(1.05);
        }
        
        .admin-button.delete {
          background-color: rgba(234, 67, 53, 0.7);
        }
        
        .admin-button.delete:hover {
          background-color: rgba(234, 67, 53, 0.9);
        }
        
        .admin-button.edit {
          background-color: rgba(251, 188, 5, 0.7);
        }
        
        .admin-button.edit:hover {
          background-color: rgba(251, 188, 5, 0.9);
        }
        
        .admin-button.cancel {
          background-color: rgba(158, 158, 158, 0.7);
        }
        
        .admin-button.cancel:hover {
          background-color: rgba(158, 158, 158, 0.9);
        }
        
        .admin-table-container {
          overflow-x: auto;
          border-radius: 8px;
          background-color: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .admin-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .admin-table th, .admin-table td {
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 12px;
          text-align: left;
        }
        
        .admin-table th {
          background-color: rgba(0, 0, 0, 0.5);
          color: rgba(255, 255, 255, 0.9);
        }
        
        .admin-table tr:hover {
          background-color: rgba(255, 255, 255, 0.05);
        }
        
        .admin-loading {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
          font-size: 20px;
          color: white;
        }
        
        h1, h2 {
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
        }
        
        h1 {
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          padding-bottom: 10px;
          margin-bottom: 20px;
        }
        
        h2 {
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 15px;
        }
        
        /* Checkbox styling to match the rest of the app */
        input[type="checkbox"] {
          appearance: none;
          -webkit-appearance: none;
          width: 16px;
          height: 16px;
          border: 1px solid rgba(255, 255, 255, 0.5);
          border-radius: 3px;
          outline: none;
          cursor: pointer;
          position: relative;
          vertical-align: middle;
          margin-right: 8px;
          background-color: rgba(0, 0, 0, 0.2);
          transition: all 0.2s ease;
        }
        
        input[type="checkbox"]:checked {
          background-color: rgba(255, 255, 255, 0.5);
          border-color: rgba(255, 255, 255, 0.7);
        }
        
        input[type="checkbox"]:checked::after {
          content: '✓';
          position: absolute;
          color: white;
          font-size: 12px;
          top: 0;
          left: 2px;
        }
        
        input[type="checkbox"]:hover {
          border-color: rgba(255, 255, 255, 0.8);
          background-color: rgba(255, 255, 255, 0.1);
        }
        
        /* File input styling */
        .file-input-container {
          position: relative;
          margin-top: 5px;
        }
        
        .file-input {
          position: absolute;
          left: -9999px;
          opacity: 0;
          width: 0.1px;
          height: 0.1px;
        }
        
        .file-input-label {
          display: inline-block;
          padding: 10px 15px;
          background-color: rgba(0, 0, 0, 0.3);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          cursor: pointer;
          width: 100%;
          transition: all 0.3s ease;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .file-input-label:hover {
          background-color: rgba(0, 0, 0, 0.5);
          border-color: rgba(255, 255, 255, 0.4);
        }
        
        .admin-button.loading {
          position: relative;
          color: transparent;
        }
        
        .admin-button.loading:after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          margin: -0.5em 0 0 -0.5em;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          width: 1em;
          height: 1em;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .admin-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .admin-button.logout {
          background-color: rgba(66, 133, 244, 0.7);
        }
        
        .admin-button.logout:hover {
          background-color: rgba(66, 133, 244, 0.9);
        }
        
        /* Thumbnail styling */
        .thumbnail-cell {
          width: 120px;
          height: 67px;
        }
        
        .video-thumbnail {
          width: 120px;
          height: 67px;
          object-fit: cover;
          border-radius: 4px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .no-thumbnail {
          width: 120px;
          height: 67px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: rgba(0, 0, 0, 0.3);
          color: rgba(255, 255, 255, 0.5);
          font-size: 12px;
          border-radius: 4px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .chart-container {
          background-color: rgba(0, 0, 0, 0.3);
          border-radius: 8px;
          padding: 15px 15px 10px 15px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
          min-height: 450px;
          display: flex;
          flex-direction: column;
        }
        
        .chart-info {
          margin-top: 10px;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          text-align: center;
        }
        
        .chart-filters {
          margin-bottom: 15px;
          display: flex;
          justify-content: flex-end;
        }
        
        .date-range-selector {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .date-range-select {
          padding: 8px 12px;
          background-color: rgba(0, 0, 0, 0.3);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 6px;
          outline: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .date-range-select:hover {
          border-color: rgba(255, 255, 255, 0.4);
        }
        
        .date-range-select:focus {
          border-color: rgba(255, 255, 255, 0.6);
          box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </div>
  );
};

export default Admin; 